/* istanbul ignore file */
import React, { useRef, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import {
  APIProvider,
  Map as GoogleMap,
  AdvancedMarker,
  AdvancedMarkerAnchorPoint,
  ControlPosition,
  useMap,
} from '@vis.gl/react-google-maps'
import classnames from 'classnames/bind'
import NotAvailable from '../notAvailable'
import styles from './index.scss'

const cx = classnames.bind(styles)

const GOOGLE_MAPS_LIBRARIES = ['places']

const Marker = () => (
  <div
    style={{
      marginLeft: -10,
      marginTop: -10,
      backgroundColor: 'red',
      width: 20,
      height: 20,
      borderRadius: 20,
    }}
  />
)

/**
 * Sets up the Street View panorama once the map instance is ready, replacing the
 * legacy google-map-react onGoogleApiLoaded({ map, maps }) callback. Under React
 * 18 that callback detached from the concurrent hydrateRoot tree. useMap()
 * returns the live google.maps.Map only after the Maps JS API has loaded.
 *
 * Implementation details as per
 * https://github.com/istarkov/google-map-react/issues/251
 * https://developers.google.com/maps/documentation/javascript/examples/streetview-service
 */
const StreetViewBridge = ({ lat, lng, panoRef, onUnavailable }) => {
  const map = useMap()

  useEffect(() => {
    if (!map) return

    const sv = new window.google.maps.StreetViewService()
    sv.getPanorama({ location: { lat, lng }, radius: 50 }, (data, status) => {
      if (status === window.google.maps.StreetViewStatus.OK) {
        const panorama = new window.google.maps.StreetViewPanorama(
          panoRef.current,
          {
            position: { lat, lng },
            pov: { heading: 270, pitch: 0 },
            visible: true,
          }
        )
        panorama.setPano(data.location.pano)
        panorama.setPov({ heading: 270, pitch: 0 })
        panorama.setVisible(true)
      } else {
        onUnavailable()
      }
    })
  }, [map, lat, lng, panoRef, onUnavailable])

  return null
}

StreetViewBridge.propTypes = {
  lat: PropTypes.number.isRequired,
  lng: PropTypes.number.isRequired,
  panoRef: PropTypes.object.isRequired,
  onUnavailable: PropTypes.func.isRequired,
}

const Map = ({
  children,
  apiKey,
  mapId = 'DEMO_MAP_ID',
  lat,
  lng,
  zoom = 18,
  className,
  elmId,
  streetview = false,
}) => {
  const panoRef = useRef(null)
  const [streetViewAvailable, setStreetViewAvailable] = useState(true)

  if (streetview && !streetViewAvailable) {
    return <NotAvailable text='Street View Not Available' />
  }

  // Never mount APIProvider without a key: an empty-key script load poisons the
  // singleton Maps script for the whole page (all other maps then fail auth).
  if (!apiKey) {
    return (
      <NotAvailable
        text={streetview ? 'Street View Not Available' : 'Map Not Available'}
      />
    )
  }

  const markerChild = children || <Marker />

  return (
    <div className={className} data-elm-id={elmId}>
      <APIProvider
        apiKey={apiKey}
        libraries={GOOGLE_MAPS_LIBRARIES}
        language='en'
        version='weekly'
      >
        <GoogleMap
          className={cx('maps')}
          mapId={mapId}
          defaultCenter={{ lat, lng }}
          defaultZoom={zoom}
          streetViewControl
          zoomControl
          zoomControlOptions={{ position: ControlPosition.LEFT_BOTTOM }}
          gestureHandling='greedy'
          rotateControl
          mapTypeControl
          style={{ width: '100%', height: '100%' }}
        >
          <AdvancedMarker
            position={{ lat, lng }}
            anchorPoint={AdvancedMarkerAnchorPoint.BOTTOM_CENTER}
          >
            {markerChild}
          </AdvancedMarker>
          {streetview && (
            <StreetViewBridge
              lat={lat}
              lng={lng}
              panoRef={panoRef}
              onUnavailable={() => setStreetViewAvailable(false)}
            />
          )}
        </GoogleMap>
      </APIProvider>
      {streetview && (
        <div
          ref={panoRef}
          style={{
            position: 'absolute',
            top: 0,
            height: '100%',
            width: '100%',
          }}
        />
      )}
    </div>
  )
}

Map.propTypes = {
  apiKey: PropTypes.string,
  mapId: PropTypes.string,
  lat: PropTypes.number.isRequired,
  lng: PropTypes.number.isRequired,
  zoom: PropTypes.number,
  streetview: PropTypes.bool,
  children: PropTypes.element,
  elmId: PropTypes.string,
  className: PropTypes.string,
}

export default Map
